import { UnitType as ApiUnitType } from "@novorender/api";
import convert, { type Unit as ConvertUnit } from "convert";

import { type useUom } from "./hooks/useUom";
import { type FormatOptions, Unit as UomUnit, UNIT_TYPE_BY_ABBREVIATION, UnitSystem, UnitType } from "./spec";

export { UnitSystem, Unit as UomUnit } from "./spec";

export type DisplayUnit = "m" | "mm" | "ftin";

// These are units that the `convert` package does not handle. We have to be
// careful to not throw errors but also not misconvert them.
export const PROBLEM_UOMS: readonly UomUnit[] = [
    UomUnit.uin,
    UomUnit.uin2,
    UomUnit.uin3,
    UomUnit.mil,
    UomUnit.mil2,
    UomUnit.mil3,
    UomUnit.um,
    UomUnit.um2,
    UomUnit.um3,
] as const;

export function convertUnit(value: number, fromUnit: UomUnit | string, toUnit: UomUnit | string): number {
    let result: number;
    try {
        result = convert(value, fromUnit as ConvertUnit).to(toUnit as ConvertUnit);
    } catch (error: unknown) {
        if (error instanceof Error && error.message.includes("is not a valid unit")) {
            result = value;
        } else {
            throw error;
        }
    }
    return result;
}

export function convertUnitSystemToDisplayUnit(unitSystem: UnitSystem): DisplayUnit {
    if (unitSystem === UnitSystem.imperial) {
        return "ftin";
    } else if (unitSystem === UnitSystem.metricMM) {
        return "mm";
    }
    return "m";
}

export function convertAndFormatApiNumber(
    uom: ReturnType<typeof useUom>,
    value: number,
    unitType: ApiUnitType,
    formatOpts?: FormatOptions,
) {
    return uom.format(uom.convertToUi(value, unitType as UnitType), unitType as UnitType, formatOpts);
}

export function convertAndFormat(
    uom: ReturnType<typeof useUom>,
    value: number,
    unitType: UnitType,
    formatOpts?: FormatOptions,
) {
    return uom.format(uom.convertToUi(value, unitType), unitType, formatOpts);
}

export function convertAndFormatForInput(
    uom: ReturnType<typeof useUom>,
    value: number,
    unitType: UnitType,
    formatOpts?: FormatOptions,
) {
    return uom.formatForInput(uom.convertToUi(value, unitType), unitType, formatOpts);
}

export function extractPropertyValueAndUnitType(value: string) {
    const parts = value.split("@", 2);
    if (parts.length < 2) {
        return;
    }
    const number = Number(parts[0]);
    const unitType = UNIT_TYPE_BY_ABBREVIATION.get(parts[1]);
    if (Number.isNaN(number) || !unitType) {
        return;
    }

    return { value: number, unitType };
}
